import React from 'react';
import { SHIFT_TYPE_LABELS, WORK_TYPE_LABELS, DRIVE_TYPE_LABELS, formatDate, formatPhone } from '../utils/formatters';
import useModalAccessibility from '../hooks/useModalAccessibility';
import './ShiftDetailsModal.css';

const FEATURE_LABELS = {
  standard: 'Standard',
  hybrid: 'Hybrid',
  electric: 'Electric',
  wav: 'WAV (Wheelchair Accessible)'
};

const ShiftDetailsModal = ({ shift, onClose, userRole }) => {
  const modalRef = useModalAccessibility(onClose);

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="shift-details-modal car-listing-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shift-details-title"
      >
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close dialog"
        >
          &times;
        </button>

        {/* Icon + Title Header */}
        <div className="listing-header">
          <div className="listing-icon car-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
            </svg>
          </div>
          <div className="listing-header-info">
            <h2 id="shift-details-title" className="listing-name">{shift.title}</h2>
            <div className="listing-meta">
              {shift.fleet_name && <span className="meta-item">by {shift.fleet_name}</span>}
              <span className="meta-item">{SHIFT_TYPE_LABELS[shift.shift_type]}</span>
              <span className="meta-item">{formatDate(shift.created_at)}</span>
            </div>
          </div>
        </div>

        {shift.description && (
          <p className="shift-description">{shift.description}</p>
        )}

        <div className="section-divider" />

        <div className="details-grid">
          <div className="detail-item">
            <span className="detail-label">Work Type</span>
            <span className="detail-value">{WORK_TYPE_LABELS[shift.vehicle_type]}</span>
          </div>

          {shift.drive_type && shift.drive_type !== 'standard' && (
            <div className="detail-item">
              <span className="detail-label">Drive Type</span>
              <span className="detail-value">{DRIVE_TYPE_LABELS[shift.drive_type] || shift.drive_type}</span>
            </div>
          )}

          {shift.wav ? (
            <div className="detail-item">
              <span className="detail-label">Accessibility</span>
              <span className="detail-value">WAV (Wheelchair Accessible)</span>
            </div>
          ) : null}

          {shift.vehicle_make && (
            <div className="detail-item">
              <span className="detail-label">Vehicle</span>
              <span className="detail-value vehicle-info">
                {shift.vehicle_year} {shift.vehicle_make} {shift.vehicle_model}
                {shift.vehicle_features && (
                  <span className={`feature-badge feature-${shift.vehicle_features}`}>
                    {FEATURE_LABELS[shift.vehicle_features] || shift.vehicle_features}
                  </span>
                )}
              </span>
            </div>
          )}

          {shift.garage_address && (
            <div className="detail-item">
              <span className="detail-label">Location</span>
              <span className="detail-value">{shift.garage_address}</span>
            </div>
          )}

          {shift.pay_rate && (
            <div className="detail-item">
              <span className="detail-label">Rate</span>
              <span className="detail-value highlight">
                ${shift.pay_rate}{shift.pay_type === 'weekly' ? '/week' : '/day'}
              </span>
            </div>
          )}

        </div>

        <div className="section-divider" />

        <div className="fleet-section">
          <span className="fleet-label">Posted by</span>
          <div className="fleet-info">
            <div className="fleet-avatar">
              {shift.fleet_name.charAt(0).toUpperCase()}
            </div>
            <div className="fleet-details">
              <div className="fleet-details-row">
                <span className="fleet-name">{shift.fleet_name}</span>
                {shift.medallion_number && (
                  <span className="fleet-medallion">Medallion #{shift.medallion_number}</span>
                )}
              </div>
              <div className="fleet-details-row">
                {shift.fleet_phone && (
                  <span className="fleet-contact-row">
                    <span className="contact-label">Phone:</span>
                    <a href={`tel:${shift.fleet_phone}`} className="fleet-contact-link">{formatPhone(shift.fleet_phone)}</a>
                  </span>
                )}
                {shift.fleet_email && (
                  <span className="fleet-contact-row">
                    <span className="contact-label">Email:</span>
                    <a href={`mailto:${shift.fleet_email}`} className="fleet-contact-link">{shift.fleet_email}</a>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftDetailsModal;
